import React, {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {useDarkMode} from "../../DarkModeContext";
import {
  Nav,
  NavLogo,
  NavLogoLabel,
  NavLink,
  NavItemLabel,
  NavExpandToggle,
  NavSubMenu,
  NavSubLink,
  FaChevronDown,
  FaChevronUp,
  Bars,
  NavMenu,
  NavBtn,
  NavBtnLink,
} from "./NavbarElements";
import Logo from '../../assets/logo.png';
import CitySelect from "./CitySelect";
import * as axios from "axios";
import {Switch} from "@material-ui/core";
import uuid from "uuid";
import {
  FaHome,
  FaStore,
  FaClipboardList,
  FaBook,
  FaTruck,
  FaUserCircle,
  FaUsers,
  FaTicketAlt,
  FaChartBar,
  FaFileInvoiceDollar,
  FaCog,
} from "react-icons/fa";

const getUnconfirmed = async () => {
  const unconfirmedResponse = await axios.default.get(`https://api.appetito.ro/v1/admin/unconfirmed/orders/count`);
  return unconfirmedResponse.data.count;
}

// A single top-level nav row, with an optional set of collapsible submenu
// links (chevron-toggled). A header-only item (subItems but no `to`, e.g.
// "Livratori") isn't a real route, so react-router's default active-match
// against the "#" placeholder we give it as `to` matches every page —
// isActive overrides that to reflect whether one of its actual subItem
// routes is the current page instead.
const NavItem = ({to, icon: Icon, label, badge, subItems, dark}) => {
  const location = useLocation();
  const hasSubItems = subItems && subItems.length > 0;
  const [expanded, setExpanded] = useState(
    hasSubItems ? subItems.some(item => location.pathname === item.to) : false
  );

  const toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded(!expanded);
  };

  const headerIsActive = hasSubItems && !to
    ? () => subItems.some(item => location.pathname === item.to)
    : undefined;

  return (
    <>
      <NavLink to={to || '#'} $dark={dark} onClick={hasSubItems && !to ? toggle : undefined} isActive={headerIsActive}>
        <Icon size={18}/>
        <NavItemLabel>{label}</NavItemLabel>
        {badge}
        {hasSubItems && (
          <NavExpandToggle type="button" $dark={dark} onClick={toggle}>
            {expanded ? <FaChevronUp size={12}/> : <FaChevronDown size={12}/>}
          </NavExpandToggle>
        )}
      </NavLink>
      {hasSubItems && expanded && (
        <NavSubMenu $dark={dark}>
          {subItems.map(item => (
            <NavSubLink key={item.to} to={item.to} $dark={dark}>{item.label}</NavSubLink>
          ))}
        </NavSubMenu>
      )}
    </>
  );
};

const Navbar = (data) => {
  const [count, setCount] = useState();
  const [isAdmin, setAdmin] = useState();
  const [closedForVillages, setClosedForVillages] = useState(false);
  // Shared with ThemedApp (Containers/App.js), which builds the MUI theme
  // off the same state — see DarkModeContext.
  const {darkMode: isDarkMode, toggleDarkMode} = useDarkMode();

  useEffect(() => {
    const getCount = async () => {
      const unconfirmedResponse = await axios.default.get(`https://api.appetito.ro/v1/admin/unconfirmed/orders/count`);
      setCount(unconfirmedResponse.data.count > 0 ? unconfirmedResponse.data.count : '');
      const isClosedForVillages = await axios.default.get(`https://api.appetito.ro/v1/admin/restaurants/closed-for-village`)
      const isDeliveryAvailable = await axios.default.get(`https://api.appetito.ro/v1/admin/restaurants/delivery-available`)
      setClosedForVillages(isClosedForVillages.data.isClosed || !isDeliveryAvailable.data.available)
    }
    const interval = setInterval(getCount, 5000);
    getCount();
    const isAdmin = localStorage.getItem('admin') === 'true';
    setAdmin(isAdmin);
    return () => clearInterval(interval);
  }, []);
  return (
    <>
      <Nav style={{backgroundColor: isDarkMode ? '#000' : '#fff'}}>
        <NavLogo to="/">
          <img style={{width: 160}} src={Logo} alt={'logo'}/>
          <NavLogoLabel>Admin</NavLogoLabel>
        </NavLogo>
        <CitySelect/>
        <Bars/>

        <NavMenu>
          <NavItem to="/dashboard" icon={FaHome} label="Dashboard" dark={isDarkMode}/>
          <NavItem
            to="/restaurants"
            icon={FaStore}
            label="Restaurante"
            dark={isDarkMode}
            badge={closedForVillages && <div style={{
              marginLeft: 5,
              backgroundColor: '#DA7D46',
              color: '#fff',
              borderRadius: 9,
              width: 18,
              textAlign: 'center'
            }}>
              !
            </div>}
          />
          <NavItem
            to="/orders"
            icon={FaClipboardList}
            label="Comenzi"
            dark={isDarkMode}
            badge={<div style={{
              marginLeft: 5,
              backgroundColor: '#DA7D46',
              color: '#fff',
              paddingLeft: 5,
              paddingRight: 5,
              borderRadius: 20
            }}>
              {count}
            </div>}
          />
          {isAdmin && <NavItem to="/menu" icon={FaBook} label="Meniu" dark={isDarkMode}/>}
          <NavItem
            icon={FaTruck}
            label="Livratori"
            dark={isDarkMode}
            subItems={[
              {to: "/deliverers/personal", label: "Personal"},
              {to: "/deliverers/program", label: "Program"},
              {to: "/deliverers/masini", label: "Mașini"},
              {to: "/deliverers/harta", label: "Hartă"},
              {to: "/deliverers/statistici", label: "Statistici"},
              {to: "/deliverers/bonus", label: "Bonus"},
              ...(isAdmin ? [{to: "/deliverers/salarii", label: "Salarii"}] : []),
            ]}
          />
          <NavItem to="/users" icon={FaUsers} label="Utilizatori" dark={isDarkMode}/>
          <NavItem to="/info-client" icon={FaUserCircle} label="Info client" dark={isDarkMode}/>
          <NavItem to="/vouchers" icon={FaTicketAlt} label="Vouchere" dark={isDarkMode}/>
          {isAdmin && (
            <NavItem
              icon={FaChartBar}
              label="Statistici"
              dark={isDarkMode}
              subItems={[
                {to: "/statistics-new", label: "Statistici"},
                {to: "/statistics-v2", label: "Statistici V2"},
              ]}
            />
          )}
          {isAdmin && <NavItem to="/invoices" icon={FaFileInvoiceDollar} label="Facturi" dark={isDarkMode}/>}
          <NavItem
            icon={FaCog}
            label="Setări"
            dark={isDarkMode}
            subItems={[
              {to: "/app-settings/aplicatie", label: "Aplicație"},
              {to: "/app-settings/lista-neagra", label: "Listă neagră"},
              ...(isAdmin ? [
                {to: "/app-settings/taxe", label: "Taxe"},
                {to: "/app-settings/bannere", label: "Bannere"},
                {to: "/app-settings/targeturi", label: "Targeturi"},
                {to: "/app-settings/orase", label: "Orașe"},
                {to: "/app-settings/roata", label: "Roata"},
              ] : []),
            ]}
          />
          <div style={{
            alignItems: "center",
            marginTop: 25,
            display: "flex",
            fontSize: 15,
            color: isDarkMode ? 'white' : 'black'
          }}>{'Darkula'}
            <Switch
              checked={isDarkMode}
              onChange={toggleDarkMode}
            /></div>
        </NavMenu>
      </Nav>
    </>
  );
};
export default Navbar;
